import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "../components/ui/calendar";
import { Check, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const TIMES = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

const SERVICES = [
  "Remote Office Administration",
  "Customer Support",
  "Bookkeeping Support",
  "Trucking Office Administration",
  "HR & Payroll",
  "Document Management",
  "Weekly Office Visits (GTA)",
  "Other / Not sure",
];

function fmt(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function Booking() {
  const [step, setStep] = useState(1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and time.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        ...form,
        preferred_date: date.toISOString().slice(0, 10),
        preferred_time: time,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit booking.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen pt-32 pb-20 bg-secondary/30" data-testid="booking-success">
        <div className="container-apex max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-apex p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} />
            </div>
            <h1 className="heading-display text-4xl mt-6">Your consultation is reserved.</h1>
            <p className="mt-4 text-muted-foreground">
              We've sent a confirmation to <span className="font-semibold text-foreground">{form.email || "your email"}</span>. A team member will confirm the exact time and send a calendar invite shortly.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 text-sm bg-secondary px-4 py-2 rounded-full">
              <Clock size={14} className="text-[#16A34A]" /> {fmt(date)} · {time}
            </div>
            <div className="mt-8">
              <a href="/" className="btn-primary">Back to home</a>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-28 pb-20 bg-secondary/30" data-testid="booking-page">
      <div className="container-apex">
        <div className="max-w-2xl">
          <div className="overline">Free Consultation</div>
          <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl mt-5">
            Book your <span className="text-[#16A34A]">30-minute</span> call.
          </h1>
          <p className="mt-5 text-muted-foreground text-lg">
            No-pressure, no-obligation. We'll learn your business and outline how Admin Apex can help.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 card-apex p-6 lg:p-8">
            <h3 className="font-display font-bold text-xl">1. Pick a date</h3>
            <div className="mt-4 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < today || d.getDay() === 0 || d.getDay() === 6}
                data-testid="booking-calendar"
                className="rounded-xl border border-border"
              />
            </div>

            <h3 className="font-display font-bold text-xl mt-8">2. Choose a time</h3>
            <div className="mt-4 grid grid-cols-4 gap-2">
              {TIMES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTime(t)}
                  data-testid={`time-${t}`}
                  className={`px-3 py-3 rounded-xl text-sm font-semibold border transition ${time === t ? "bg-[#16A34A] text-white border-[#16A34A]" : "border-border hover:border-[#16A34A]"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">All times Eastern (Toronto). Mon–Fri.</p>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 card-apex p-6 lg:p-10" data-testid="booking-form">
            <h3 className="font-display font-bold text-xl">3. Tell us about you</h3>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <FInput label="Full name" id="b-name" required value={form.name} onChange={set("name")} />
              <FInput label="Email" id="b-email" type="email" required value={form.email} onChange={set("email")} />
              <FInput label="Phone" id="b-phone" value={form.phone} onChange={set("phone")} />
              <FInput label="Company" id="b-company" value={form.company} onChange={set("company")} />
              <div className="sm:col-span-2">
                <Label>Service of interest</Label>
                <select
                  value={form.service}
                  onChange={set("service")}
                  data-testid="b-service"
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
                >
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label>Notes (optional)</Label>
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={set("notes")}
                  data-testid="b-notes"
                  placeholder="Anything we should know before the call?"
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4 pt-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {date ? (
                  <span className="inline-flex items-center gap-2"><Check size={14} className="text-[#16A34A]" /> {fmt(date)} {time && `· ${time}`}</span>
                ) : "Pick a date & time to continue"}
              </div>
              <button type="submit" disabled={loading || !date || !time} data-testid="booking-submit" className="btn-accent disabled:opacity-50">
                {loading ? "Reserving…" : "Confirm Booking"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

function Label({ children }) {
  return <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 block">{children}</label>;
}
function FInput({ label, id, type = "text", required, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={id}
        className="w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
      />
    </div>
  );
}
